import { useEffect, useState } from "react";
import api, { fmtMXN, PROJECT_STATUS } from "@/lib/api";
import PageHeader, { StatusBadge } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { HardHat, Receipt, Wallet, Building2, RefreshCw, AlertTriangle, Plus, ClipboardCheck, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import UpcomingPayments from "@/components/UpcomingPayments";
import MyCashPanel from "@/components/MyCashPanel";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [time, setTime] = useState(new Date());

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/dashboard/stats"), api.get("/projects")]);
    setStats(s.data);
    setProjects(p.data.slice(0, 5));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const timeStr = time.toLocaleTimeString("es-MX", { hour12: false });
  const dateStr = time.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div data-testid="dashboard-page">
      <PageHeader testId="dashboard-header"
        subtitle={stats?.scope === "personal" ? `// MIS PROYECTOS · ${user?.name}` : `Bienvenido, ${user?.name}`}
        title="Panel de control" />
      <div className="px-6 lg:px-8 py-6 space-y-6">
        {/* Quick Actions — visibles SOLO en móvil/tablet */}
        <div data-testid="mobile-quick-actions" className="lg:hidden grid grid-cols-2 gap-3">
          <Link
            to="/expenses?new=1"
            data-testid="quick-action-expense"
            className="panel-strong relative overflow-hidden p-4 flex flex-col items-center text-center gap-2 transition-transform active:scale-95"
            style={{ borderColor: "rgba(34,211,238,0.45)", background: "linear-gradient(135deg, rgba(34,211,238,0.18), rgba(34,211,238,0.03))" }}
          >
            <div className="w-12 h-12 rounded-sm flex items-center justify-center"
              style={{ background: "rgba(34,211,238,0.18)", color: "var(--cyan)", border: "1px solid rgba(34,211,238,0.4)" }}>
              <Plus className="w-6 h-6" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-slate-100">Capturar gasto</span>
          </Link>
          <Link
            to="/payroll?tab=attendance"
            data-testid="quick-action-attendance"
            className="panel-strong relative overflow-hidden p-4 flex flex-col items-center text-center gap-2 transition-transform active:scale-95"
            style={{ borderColor: "rgba(99,102,241,0.45)", background: "linear-gradient(135deg, rgba(99,102,241,0.18), rgba(99,102,241,0.03))" }}
          >
            <div className="w-12 h-12 rounded-sm flex items-center justify-center"
              style={{ background: "rgba(99,102,241,0.18)", color: "#a5b4fc", border: "1px solid rgba(99,102,241,0.4)" }}>
              <ClipboardCheck className="w-6 h-6" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-slate-100">Marcar asistencia</span>
          </Link>
          <Link
            to="/projects"
            data-testid="quick-action-projects"
            className="panel-strong relative overflow-hidden p-4 flex flex-col items-center text-center gap-2 transition-transform active:scale-95"
            style={{ borderColor: "rgba(217,70,239,0.45)", background: "linear-gradient(135deg, rgba(217,70,239,0.18), rgba(217,70,239,0.03))" }}
          >
            <div className="w-12 h-12 rounded-sm flex items-center justify-center"
              style={{ background: "rgba(217,70,239,0.18)", color: "var(--magenta)", border: "1px solid rgba(217,70,239,0.4)" }}>
              <HardHat className="w-6 h-6" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-slate-100">Mis obras</span>
          </Link>
          <Link
            to={projects[0]?.id ? `/projects/${projects[0].id}/progress-mobile` : "/projects"}
            data-testid="quick-action-progress"
            className="panel-strong relative overflow-hidden p-4 flex flex-col items-center text-center gap-2 transition-transform active:scale-95"
            style={{ borderColor: "rgba(22,163,74,0.45)", background: "linear-gradient(135deg, rgba(22,163,74,0.18), rgba(22,163,74,0.03))" }}
          >
            <div className="w-12 h-12 rounded-sm flex items-center justify-center"
              style={{ background: "rgba(22,163,74,0.18)", color: "#86efac", border: "1px solid rgba(22,163,74,0.4)" }}>
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-slate-100">Capturar avance</span>
          </Link>
        </div>

        {/* Alerta: obras que excedieron presupuesto */}
        {(stats?.at_risk_projects || []).length > 0 && (
          <div
            data-testid="at-risk-banner"
            className="panel-strong relative overflow-hidden"
            style={{
              borderColor: "rgba(220,38,38,0.55)",
              background: "linear-gradient(90deg, rgba(220,38,38,0.18), rgba(220,38,38,0.04))",
              boxShadow: "inset 0 0 22px rgba(220,38,38,0.18)",
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #dc2626, transparent)" }} />
            <div className="px-5 py-4 flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0"
                style={{ background: "rgba(220,38,38,0.18)", color: "#fca5a5", border: "1px solid rgba(220,38,38,0.45)" }}>
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-mono text-[10px] mb-1" style={{ color: "#fca5a5" }}>
                  // ALERTA · OBRAS SOBRE PRESUPUESTO
                </div>
                <div className="font-display font-bold text-base text-slate-100">
                  {stats.at_risk_projects.length} obra{stats.at_risk_projects.length !== 1 ? "s" : ""} excedi{stats.at_risk_projects.length !== 1 ? "eron" : "ó"} su presupuesto
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {stats.at_risk_projects.slice(0, 6).map((p) => (
                    <Link
                      key={p.id}
                      to={`/projects/${p.id}/report`}
                      data-testid={`at-risk-item-${p.id}`}
                      className="panel p-3 row-hover transition-colors"
                      style={{ borderColor: "rgba(220,38,38,0.35)" }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>{p.code}</span>
                        <span className="font-mono text-xs font-bold" style={{ color: "#fca5a5" }}>
                          +{p.over_pct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="text-xs font-semibold text-slate-200 mt-1 truncate">{p.name}</div>
                      <div className="text-[11px] text-slate-400 mt-1 flex items-center justify-between">
                        <span>Pres: {fmtMXN(p.budget)}</span>
                        <span style={{ color: "#fca5a5" }}>Gastado: {fmtMXN(p.committed)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Kpi testId="kpi-active-projects" label="// PROYECTOS ACTIVOS"
              value={stats?.active_projects ?? "—"} sub={`${stats?.total_projects ?? 0} totales`}
              Icon={HardHat} accent="cyan" live />
            <Kpi testId="kpi-budget" label="// PRESUPUESTO TOTAL"
              value={fmtMXN(stats?.total_budget)} sub="Asignado en obra"
              Icon={Building2} accent="magenta" />
            <Kpi testId="kpi-expenses" label="// GASTOS EJECUTADOS"
              value={fmtMXN(stats?.total_expenses)} sub={`${stats?.pending_expenses ?? 0} por aprobar`}
              Icon={Receipt} accent="amber" />
            <Kpi testId="kpi-payroll" label="// NÓMINA PAGADA"
              value={fmtMXN(stats?.payroll_cost)} sub={`${stats?.active_employees ?? 0} empleados`}
              Icon={Wallet} accent="green" />
          </div>

          {/* Right column: System time */}
          <div className="panel-strong p-6 flex flex-col scan-line">
            <div className="label-mono mb-3 flex items-center justify-between">
              <span>// SYSTEM TIME</span>
              <RefreshCw className="w-3 h-3 text-slate-500 cursor-pointer hover:rotate-180 transition-transform" onClick={load} />
            </div>
            <div className="metric-value text-4xl text-cyan-neon tabular-nums my-3" style={{ color: "var(--cyan)" }}>
              {timeStr}
            </div>
            <div className="text-sm text-slate-400 mb-6 capitalize">{dateStr}</div>
            <div className="grid grid-cols-2 gap-3 mt-auto">
              <Mini label="UPTIME" value="14d 06:42" />
              <Mini label="ZONA" value="GMT-6" />
            </div>
          </div>
        </div>

        {/* Pagos próximos (admin/contador/auxiliar) */}
        {["admin", "contador", "auxiliar"].includes(user?.role) && (
          <UpcomingPayments />
        )}

        {/* Mi caja / efectivo (usuarios no-admin) */}
        {["residente", "supervisor", "contador", "auxiliar"].includes(user?.role) && (
          <MyCashPanel />
        )}

        {/* Projects + Categories */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="panel lg:col-span-2">
            <div className="panel-header px-5 py-4 flex items-center justify-between">
              <div>
                <div className="label-mono">// Proyectos recientes</div>
                <h3 className="font-display font-bold text-lg mt-0.5 tracking-wide text-slate-100">Últimas obras</h3>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 text-[10px] label-mono"><span className="live-dot" /> LIVE</span>
                <Link to="/projects" data-testid="dashboard-view-all-projects" className="text-xs font-semibold label-mono hover:underline" style={{ color: "var(--cyan)" }}>VER TODOS →</Link>
              </div>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {projects.length === 0 && (
                <div className="px-5 py-10 text-sm text-slate-500 text-center">Sin proyectos aún. Crea el primero.</div>
              )}
              {projects.map((p) => (
                <div key={p.id} className="px-5 py-4 flex items-center justify-between row-hover transition-colors"
                  style={{ borderBottom: "1px solid var(--border)" }}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="label-mono" style={{ color: "var(--cyan)" }}>{p.code}</span>
                      <StatusBadge items={PROJECT_STATUS} value={p.status} />
                    </div>
                    <div className="font-semibold text-sm text-slate-100 mt-1 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.client} · {p.address}</div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="font-mono text-sm font-bold text-slate-100">{fmtMXN(p.budget)}</div>
                    <div className="label-mono text-[10px] mt-0.5">{p.progress}% AVANCE</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="panel">
            <div className="panel-header px-5 py-4">
              <div className="label-mono">// Distribución</div>
              <h3 className="font-display font-bold text-lg mt-0.5 tracking-wide text-slate-100">Gastos por categoría</h3>
            </div>
            <div className="p-5 space-y-3.5">
              {Object.entries(stats?.expenses_by_category || {}).length === 0 && (
                <div className="text-sm text-slate-500 text-center py-6">Sin datos.</div>
              )}
              {Object.entries(stats?.expenses_by_category || {})
                .sort((a, b) => b[1] - a[1])
                .map(([cat, amount], i) => {
                  const total = Object.values(stats?.expenses_by_category || {}).reduce((a, b) => a + b, 0) || 1;
                  const pct = (amount / total) * 100;
                  const colors = ["var(--cyan)", "var(--magenta)", "var(--green)", "var(--amber)", "#a78bfa", "#fb923c", "#94a3b8"];
                  return (
                    <div key={cat}>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="font-semibold capitalize text-slate-300">{cat.replace("_", " ")}</span>
                        <span className="font-mono text-slate-200">{fmtMXN(amount)}</span>
                      </div>
                      <div className="h-1 overflow-hidden rounded-sm" style={{ background: "var(--bg-2)" }}>
                        <div className="h-full transition-all" style={{
                          width: `${pct}%`,
                          background: colors[i % colors.length],
                          boxShadow: `0 0 8px ${colors[i % colors.length]}`,
                        }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, Icon, accent, testId, live }) {
  const accents = {
    cyan: "var(--cyan)",
    magenta: "var(--magenta)",
    amber: "var(--amber)",
    green: "var(--green)",
  };
  const c = accents[accent];
  return (
    <div data-testid={testId} className="panel-strong p-5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${c}, transparent)` }} />
      <div className="flex items-start justify-between mb-4">
        <div className="label-mono">{label}</div>
        <div className="w-9 h-9 flex items-center justify-center rounded-sm"
          style={{ background: `${c}15`, border: `1px solid ${c}40`, color: c, boxShadow: `0 0 16px ${c}25` }}>
          <Icon className="w-[18px] h-[18px]" />
        </div>
      </div>
      <div className="metric-value text-4xl tabular-nums" style={{ color: c, textShadow: `0 0 14px ${c}40` }}>
        {value}
      </div>
      <div className="text-xs text-slate-500 mt-2 flex items-center justify-between">
        <span>{sub}</span>
        {live && <span className="flex items-center gap-1.5 label-mono text-[9px]"><span className="live-dot" /> LIVE</span>}
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="panel p-3">
      <div className="label-mono text-[9px] mb-1">{label}</div>
      <div className="font-mono text-sm font-bold text-slate-100">{value}</div>
    </div>
  );
}
